import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Moment from "moment";
import { saveAs } from "file-saver";
import useDecode from "../../../../hooks/useDecode";

import logo from "../../../../img/solimat-logo.png";
import { moneyFormatter, decimalFormatter } from "@/services/utils/useUtils";

const PrintButtonPagoDirecto = ({ dataEconomicSheet, disabled = false }) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const tokenResponse = useDecode("token");
  const tokenFullName = [
    tokenResponse?.extension_first_name,
    tokenResponse?.extension_first_last_name,
    tokenResponse?.extension_second_last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const toBase64 = (url) =>
    fetch(url)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );

  const toBase64WithTimeout = (url, ms = 3000) => {
    return Promise.race([
      toBase64(url),
      new Promise((_, reject) => setTimeout(() => reject(new Error('toBase64 timeout')), ms)),
    ]);
  };

  const generatePDF = async () => {
    try {
      const doc = new jsPDF();

      try {
        let logoBase64;
        try {
          logoBase64 = await toBase64WithTimeout(logo, 3000);
        } catch (e) {
          console.warn('generatePDF: logo conversion failed or timed out, skipping logo', e);
          logoBase64 = null;
        }

        if (logoBase64) {
          const img = new Image();
          img.src = logoBase64;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error('Image load error'));
          });

          const maxWidth = 50;
          const ratio = img.width / img.height || 1;
          const logoWidth = maxWidth;
          const logoHeight = logoWidth / ratio;

          const logoX = 20;
          const logoY = 10;
          try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
          } catch (errAdd) {
            console.warn('generatePDF: addImage failed, skipping logo', errAdd);
          }

          const headerX = logoX + logoWidth + 40;
          const headerY = 15;

          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor("#409CCE");
          doc.text("RECIBO DE ABONO PRESTACION", headerX, headerY);

          doc.setFontSize(6);
          doc.setFont("helvetica", "normal");
          doc.setTextColor("#808080");
          doc.text("SOLIMAT MUTUA COLABORADORA CON SEG. SOCIAL Nº 72", headerX, headerY + 3);
        } else {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor("#409CCE");
          doc.text("RECIBO DE ABONO PRESTACION", 20, 20);
        }
      } catch (logoError) {
        console.warn("Error con logo (outer):", logoError);
      }

      // --- Carta ---
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const startY = 50;
      const marginLeft = 20;
      const marginRight = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const maxWidth = pageWidth - marginLeft - marginRight;

      const paragraphs = [
        `Estimado Señor/a, ${tokenFullName || "No aplica"},`,
        `Por la presente le comunicamos que SOLIMAT, Mutua Colaboradora con la Seguridad Social nº 072, ha procedido al abono de la prestación económica reconocida por esta entidad con fecha ${
          dataEconomicSheet?.FechaInicioPago ? Moment(dataEconomicSheet.FechaInicioPago).format("DD/MM/YYYY") : "__/__/____"
        }.`,
        "Hemos hecho el ingreso de la cantidad que se detalla a continuación en el número de cuenta indicado por usted en el formulario de solicitud de la prestación.",
        "Los detalles del abono son los siguientes:"
      ];

      let currentY = startY;
      const lineSpacing = 4;
      const paragraphSpacing = 6;

      paragraphs.forEach((para) => {
        const lines = doc.splitTextToSize(para, maxWidth);
        doc.text(lines, marginLeft, currentY);
        currentY += lines.length * lineSpacing + paragraphSpacing;
      });

      const tableData = [
        ["Fecha Inicio Prestación Económica", dataEconomicSheet?.FechaInicioPago ? Moment(dataEconomicSheet.FechaInicioPago).format("DD/MM/YYYY") : "xx/xx/xxxx"],
        ["Contingencia", dataEconomicSheet?.ContingenciaDescripcion || "No aplica"],
        ["Motivo del Pago Directo", dataEconomicSheet?.MotivoPagoDirectoDescripcion || "No aplica"],
        ["Período Abonado", dataEconomicSheet?.FechaPeriodoInicial && dataEconomicSheet?.FechaPeriodoFinal ? `${Moment(dataEconomicSheet.FechaPeriodoInicial).format("DD/MM/YYYY")} - ${Moment(dataEconomicSheet.FechaPeriodoFinal).format("DD/MM/YYYY")}` : "No aplica"],
        ["Base Reguladora Diaria", dataEconomicSheet?.BaseReguladoraDiaria ? `${moneyFormatter.format(dataEconomicSheet.BaseReguladoraDiaria)}` : "No aplica"],
        ["I.P.R.E.M. (diario)", dataEconomicSheet?.IPREM_Diario ? `${moneyFormatter.format(dataEconomicSheet.IPREM_Diario)}` : "No aplica"],
        ["% aplicado", dataEconomicSheet?.Porcentaje1 || dataEconomicSheet?.Porcentaje2 ? `${dataEconomicSheet.Porcentaje1 || dataEconomicSheet.Porcentaje2}%` : "No aplica"],
        ["Importe por Día", dataEconomicSheet?.ImporteDia ? `${moneyFormatter.format(dataEconomicSheet.ImporteDia)}` : "No aplica"],
        ["Días Devengados", dataEconomicSheet?.DiasDevengados || "No aplica"],
        ["", ""],
        ["Retenciones:", ""],
        ["     IRPF", dataEconomicSheet?.ImporteRetenciones ? `${moneyFormatter.format(dataEconomicSheet.ImporteRetenciones)}` : "No aplica"],
        ["     Cuota Obrera", dataEconomicSheet?.ImporteCuotaObrera ? `${moneyFormatter.format(dataEconomicSheet.ImporteCuotaObrera)}` : "No aplica"],
        ["", ""],
        ["Importe Bruto", dataEconomicSheet?.Bruto ? `${moneyFormatter.format(dataEconomicSheet.Bruto)}` : "No aplica"],
        ["Importe Neto", dataEconomicSheet?.Neto ? `${moneyFormatter.format(dataEconomicSheet.Neto)}` : "No aplica"]
      ];

      const tableColumnWidth = 80;
      const tableWidth = tableColumnWidth * 2;
      const tableMarginLeft = (pageWidth - tableWidth) / 2;

      try {
        const normalizedTableData = tableData.map(row => row.map ? row.map(cell => (cell === null || cell === undefined) ? 'No aplica' : String(cell)) : row.map ? row : row);
        const flat = normalizedTableData.flat();
        const allPlaceholders = flat.length > 0 && flat.every(c => (c || '').trim() === 'No aplica');
        if (allPlaceholders) {
          normalizedTableData.unshift(["(No hay datos)", ""]);
        }
        const pageHeight = doc.internal.pageSize.getHeight();
        if (currentY > pageHeight - 50) currentY = pageHeight - 50;
        autoTable(doc, {
          startY: currentY,
          head: [["Concepto", "Valor"]],
          body: normalizedTableData,
          theme: 'grid',
          styles: { fontSize: 10, cellPadding: 3, textColor: [0, 0, 0], fillColor: [255, 255, 255] },
          columnStyles: { 0: { cellWidth: tableColumnWidth }, 1: { cellWidth: tableColumnWidth } },
          margin: { left: tableMarginLeft },
          headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: 'bold' },
          bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          didParseCell: function (data) {
            if (data.cell.raw === "" && data.row.index % 2 === 0) {
              data.cell.styles.fillColor = [255, 255, 255];
              data.cell.styles.minCellHeight = 5;
            }
            if (data.cell.raw === "Retenciones:") data.cell.styles.fontStyle = "bold";
            if (data.cell.raw === "Importe Bruto" || data.cell.raw === "Importe Neto")
              data.cell.styles.fontStyle = "bold";
          },
        });
        const autoY = doc.lastAutoTable && doc.lastAutoTable.finalY;
        const didAutoTableRender = !!autoY && autoY > currentY;
        if (!didAutoTableRender) {
          const drawManualTable = (rows, startX, startY, colWidth, rowHeight = 8) => {
            // header
            doc.setFillColor(230, 230, 230);
            doc.rect(startX, startY, colWidth, rowHeight, 'F');
            doc.rect(startX + colWidth, startY, colWidth, rowHeight, 'F');
            doc.setTextColor(0, 0, 0);
            doc.setFontStyle('bold');
            doc.text('Concepto', startX + 2, startY + 6);
            doc.text('Valor', startX + colWidth + 2, startY + 6);
            doc.setFontStyle('normal');
            let y = startY + rowHeight;
            for (let r = 0; r < rows.length; r++) {
              const row = rows[r];
              doc.rect(startX, y, colWidth, rowHeight);
              doc.rect(startX + colWidth, y, colWidth, rowHeight);
              const a = row[0] || '';
              const b = row[1] || '';
              doc.text(String(a), startX + 2, y + 6);
              doc.text(String(b), startX + colWidth + 2, y + 6);
              y += rowHeight;
              if (y > doc.internal.pageSize.getHeight() - 40) {
                doc.addPage();
                y = 20;
              }
            }
            return y;
          };
          try {
            const manualEndY = drawManualTable(normalizedTableData, tableMarginLeft, currentY, tableColumnWidth);
            currentY = manualEndY + 10;
          } catch (manErr) {
            console.error('generatePDF: manual table drawing failed', manErr);
          }
        }
      } catch (autoErr) {
        console.error('generatePDF: autoTable error', autoErr);
      }

      currentY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : currentY) + 20;
      
      // Guardar PDF
      const fileName = `pago_directo_${dataEconomicSheet?.PagoDirectoId || "documento"}.pdf`;
      try {
        const pdfBlob = doc.output("blob");
        saveAs(pdfBlob, fileName);
      } catch (saveErr) {
        console.warn('generatePDF: saveAs failed, attempting doc.save', saveErr);
        try {
          doc.save(fileName);
        } catch (errSave) {
          console.error('generatePDF: error saving PDF, will try opening blob url', errSave);
          try {
            const blobUrl = doc.output('bloburl');
            window.open(blobUrl);
          } catch (openErr) {
            console.error('generatePDF: failed to open blob url', openErr);
          }
        }
      }

    } catch (error) {
      console.error("Error al generar PDF:", error);
    }
  };

  const handleClick = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      await generatePDF();
    } catch (error) {
      console.error("Error en handleClick:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button type="button" className="btn-box btn-accent" onClick={handleClick} disabled={disabled || isGenerating}>
      {isGenerating ? 'Generando PDF...' : 'Descargar PDF'}
    </button>
  );
};

export default PrintButtonPagoDirecto;
